// Mock data for the Docs4AI web app

// Available frameworks and their versions
export const frameworks = [
  {
    id: "react",
    name: "React",
    description: "A JavaScript library for building user interfaces",
    logo: "/images/frameworks/react.png",
    versions: ["18.2.0", "18.0.0", "17.0.2", "16.14.0"],
    categories: ["frontend", "ui"],
  },
  {
    id: "angular",
    name: "Angular",
    description: "Platform for building web applications",
    logo: "/images/frameworks/angular.png",
    versions: ["17.0.0", "16.0.0", "15.0.0", "14.0.0"],
    categories: ["frontend", "ui"],
  },
  {
    id: "gemini",
    name: "Google Gemini",
    description: "Google's most capable AI model",
    logo: "/images/frameworks/google-gemini.png",
    versions: ["1.0.0"],
    categories: ["ai", "llm"],
  },
  {
    id: "openai",
    name: "OpenAI",
    description: "Leading AI research and deployment company",
    logo: "/images/frameworks/openai.png",
    versions: ["1.0.0"],
    categories: ["ai", "llm"],
  },
];

// Mock documentation sections
export const documentationSections = {
  react: {
    "18.2.0": [
      {
        id: "getting-started",
        title: "Getting Started",
        content: `
# Getting Started with React

React is a JavaScript library for building user interfaces. It is maintained by Facebook and a community of individual developers and companies.

## Installation

You can add React to your website in a minute. You can also create a new React app using Create React App.

\`\`\`bash
npx create-react-app my-app
cd my-app
npm start
\`\`\`

## Hello World

The smallest React example looks like this:

\`\`\`jsx
import React from 'react';
import ReactDOM from 'react-dom/client';

function HelloWorld() {
  return <h1>Hello, world!</h1>;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<HelloWorld />);
\`\`\`
        `,
      },
      {
        id: "components",
        title: "Components and Props",
        content: `
# Components and Props

Components let you split the UI into independent, reusable pieces, and think about each piece in isolation.

## Function and Class Components

The simplest way to define a component is to write a JavaScript function:

\`\`\`jsx
function Welcome(props) {
  return <h1>Hello, {props.name}</h1>;
}
\`\`\`

You can also use an ES6 class to define a component:

\`\`\`jsx
class Welcome extends React.Component {
  render() {
    return <h1>Hello, {this.props.name}</h1>;
  }
}
\`\`\`

## Rendering a Component

When React sees an element representing a user-defined component, it passes JSX attributes and children to this component as a single object called "props".

\`\`\`jsx
function Welcome(props) {
  return <h1>Hello, {props.name}</h1>;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const element = <Welcome name="Sara" />;
root.render(element);
\`\`\`
        `,
      },
      {
        id: "hooks",
        title: "Hooks",
        content: `
# Hooks

Hooks are a new addition in React 16.8. They let you use state and other React features without writing a class.

## useState

\`\`\`jsx
import React, { useState } from 'react';

function Example() {
  // Declare a new state variable, which we'll call "count"
  const [count, setCount] = useState(0);

  return (
    <div>
      <p>You clicked {count} times</p>
      <button onClick={() => setCount(count + 1)}>
        Click me
      </button>
    </div>
  );
}
\`\`\`

## useEffect

The Effect Hook lets you perform side effects in function components:

\`\`\`jsx
import React, { useState, useEffect } from 'react';

function Example() {
  const [count, setCount] = useState(0);

  // Similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    // Update the document title using the browser API
    document.title = \`You clicked \${count} times\`;
  });

  return (
    <div>
      <p>You clicked {count} times</p>
      <button onClick={() => setCount(count + 1)}>
        Click me
      </button>
    </div>
  );
}
\`\`\`
        `,
      },
    ],
  },
  nextjs: {
    "14.0.0": [
      {
        id: "getting-started",
        title: "Getting Started",
        content: `
# Getting Started with Next.js

Next.js is a React framework for production - it gives you the best developer experience with all the features you need for production.

## Installation

Create a new Next.js app using create-next-app:

\`\`\`bash
npx create-next-app@latest
# or
yarn create next-app
# or
pnpm create next-app
\`\`\`

## Pages

In Next.js, a page is a React Component exported from a file in the \`pages\` directory. Pages are associated with a route based on their file name.

\`\`\`jsx
// pages/index.js
export default function Home() {
  return <h1>Hello, Next.js!</h1>
}
\`\`\`

## App Router

Next.js 13 introduced a new App Router built on React Server Components, which supports shared layouts, nested routing, loading states, error handling, and more.

\`\`\`jsx
// app/page.js
export default function Page() {
  return <h1>Hello, Next.js App Router!</h1>
}
\`\`\`
        `,
      },
    ],
  },
};

// Subscription tiers
export const subscriptionTiers = [
  {
    id: "free",
    name: "Free",
    description: "Access both higher-tier features with limited rate limits and raw documentation retrieval for essential use.",
    price: 0,
    image: "/images/features/free-tier.svg",
    features: [
      "Access to all documentation",
      "Basic search functionality",
      "Raw documentation retrieval",
      "Direct documentation lookup",
      "Generous documentation browsing limits",
      "Documentation with source URLs",
    ],
    popular: false,
  },
  {
    id: "ai-docs",
    name: "AI Docs",
    description: "Enhanced AI-powered documentation features with Gemini Flash and increased rate limits for improved productivity.",
    price: 10,
    image: "/images/features/ai-docs-tier.svg",
    features: [
      "Everything in Free",
      "AI-powered answers with Gemini Flash",
      "Retrieval-Augmented Generation (RAG)",
      "Answers with cited references",
      "Code examples with documentation sources",
      "Fast, cost-effective responses",
      "Moderate API rate limits",
    ],
    popular: true,
  },
  {
    id: "ai-docs-pro",
    name: "AI Docs Pro",
    description: "Enjoy  AI documentation with Claude 3.7 Sonnet, priority processing, maximum rate limits for top-tier productivity.",
    price: 50,
    image: "/images/features/ai-docs-pro-tier.svg",
    features: [
      "Everything in AI Docs",
      "Premium AI with Claude 3.7 Sonnet",
      "Superior coding explanations",
      "Detailed answers for complex queries",
      "Highest rate limits",
      "Priority support",
      "Custom API integration",
      "Team collaboration features",
    ],
    popular: false,
  },
];

// Mock user data
export const mockUser = {
  id: "user123",
  name: "John Doe",
  email: "john@example.com",
  subscription: "ai-docs",
  apiKey: "sk-docs4ai-abcdef123456",
  usageStats: {
    apiCalls: {
      total: 450,
      limit: 1000,
      remaining: 550,
    },
    searches: 120,
    aiResponses: 330,
    lastActive: new Date("2025-03-10T15:30:00"),
  },
};

// Mock API usage data for charts
export const apiUsageData = [
  { date: "2025-03-01", calls: 42 },
  { date: "2025-03-02", calls: 38 },
  { date: "2025-03-03", calls: 56 },
  { date: "2025-03-04", calls: 72 },
  { date: "2025-03-05", calls: 65 },
  { date: "2025-03-06", calls: 48 },
  { date: "2025-03-07", calls: 53 },
  { date: "2025-03-08", calls: 41 },
  { date: "2025-03-09", calls: 35 },
  { date: "2025-03-10", calls: 45 },
];

// Mock API playground examples
export const apiPlaygroundExamples = [
  {
    name: "Basic Documentation Query",
    language: "javascript",
    code: `import { Docs4AI } from '@docs4ai/sdk';

const docs4ai = new Docs4AI({
  apiKey: 'YOUR_API_KEY',
});

async function getDocumentation() {
  const response = await docs4ai.query({
    framework: 'react',
    version: '18.2.0',
    query: 'How to use useState hook',
  });
  
  console.log(response.content);
  console.log('Sources:', response.sources);
}

getDocumentation();`,
  },
  {
    name: "AI-Powered Explanation",
    language: "javascript",
    code: `import { Docs4AI } from '@docs4ai/sdk';

const docs4ai = new Docs4AI({
  apiKey: 'YOUR_API_KEY',
});

async function getAIExplanation() {
  const response = await docs4ai.explain({
    framework: 'react',
    version: '18.2.0',
    query: 'Explain how useEffect cleanup works',
    model: 'gemini-flash', // or 'claude-sonnet' for Pro tier
  });
  
  console.log(response.explanation);
  console.log('Sources:', response.sources);
}

getAIExplanation();`,
  },
  {
    name: "Code Example Generation",
    language: "javascript",
    code: `import { Docs4AI } from '@docs4ai/sdk';

const docs4ai = new Docs4AI({
  apiKey: 'YOUR_API_KEY',
});

async function generateCodeExample() {
  const response = await docs4ai.generateCode({
    framework: 'react',
    version: '18.2.0',
    prompt: 'Create a counter component with useState',
    model: 'claude-sonnet', // Pro tier feature
  });
  
  console.log(response.code);
  console.log('Explanation:', response.explanation);
}

generateCodeExample();`,
  },
];

// Mock testimonials
export const testimonials = [
  {
    id: 1,
    name: "Sarah Chen",
    role: "Senior Frontend Developer",
    company: "TechCorp",
    content: "Docs4AI has completely transformed how I work with documentation. The AI-powered answers save me hours of digging through docs.",
    avatar: "/images/avatars/user-1.png",
  },
  {
    id: 2,
    name: "Michael Rodriguez",
    role: "Full Stack Engineer",
    company: "DevStudio",
    content: "The Claude 3.7 Sonnet integration in the Pro tier is mind-blowing. It understands complex coding questions and provides detailed explanations.",
    avatar: "/images/avatars/user-2.png",
  },
  {
    id: 3,
    name: "Aisha Johnson",
    role: "CTO",
    company: "StartupX",
    content: "We've integrated Docs4AI into our development workflow, and it's been a game-changer for our team's productivity.",
    avatar: "/images/avatars/user-3.png",
  },
];
